import { isWithinInterval } from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { updateOrder } from '@/features/domain/order'
import { selectCalendarRange } from '@/features/domain/ui'
import { selectUserConfiguration } from '@/features/domain/user'

import { useResetEditingState } from '@/atoms'
import { formatDate, useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'
import { dateUtils } from '@/utils'

import { CustomNotification } from '../components/CustomNotification'
import { useOnChangeCalenderRange } from './useOnChangeCalenderRange'

export const useOnUpdateOrder = (orderId: string) => {
  const dispatch = useAppDispatch()

  const stopEditing = useResetEditingState()

  const { onChangeCalenderRange } = useOnChangeCalenderRange()

  const mounted = useRef<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const toast = useNotification()
  const calendarRange = useSelector(selectCalendarRange)
  const userConfiguration = useSelector(selectUserConfiguration)

  const onUpdateOrder = useCallback(
    async (updateRequest: uui.domain.ui.forms.OrderUpdate, pristine?: boolean) => {
      try {
        if (pristine) {
          stopEditing()
          return
        }

        setSubmitting(true)

        const thunkResult = await dispatch(updateOrder(updateRequest))

        if (mounted.current) {
          setSubmitting(false)
        }

        if (!updateOrder.fulfilled.match(thunkResult)) {
          throw new Error(thunkResult.payload?.message ?? 'Internal error')
        }

        if (updateRequest.type === 'recurrence') {
          const eligibilityType = updateRequest.orderUpdateChanges.update?.order?.eligibility?.type
          const eligibleDates =
            eligibilityType === 'on'
              ? updateRequest.orderUpdateChanges.update?.order?.eligibility?.dates
              : null
          const eligibleDate = eligibleDates ? eligibleDates[0] : undefined

          if (eligibleDate) {
            //check if the eligible date is in the opened calendar range
            const isEligibleDateIsInOpenedCalendarRange = isWithinInterval(
              dateUtils.parseDate(eligibleDate),
              {
                start: dateUtils.parseDate(calendarRange.minDate),
                end: dateUtils.parseDate(calendarRange.maxDate),
              },
            )

            const formattedEligibleDate = formatDate(eligibleDate, false, userConfiguration)

            //display the toast with the link to navigate to the edited order if the eligible date is outside the opened calendar range
            if (!isEligibleDateIsInOpenedCalendarRange) {
              toast.success(CustomNotification, {
                data: {
                  formattedDate: formattedEligibleDate,
                  onClick: () => onChangeCalenderRange(eligibleDate, eligibleDate, orderId),
                },
              })
            }
          }
        }
        stopEditing()
      } catch (e) {
        toast.error(e.message)
      }
    },
    [dispatch, stopEditing, toast],
  )

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  return {
    submitting,
    onUpdateOrder,
  }
}
